import { message, Modal, Spin } from 'antd';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Api from '../../../api/Api';
import useGetUserProfile from '../../../queries/useGetUserProfile';
import useUpdateUserProfileMutation from '../../../queries/useUpdateUserProfileMutation';
import useAuthStore from '../../../store/authStore';
import useHomeStore from '../../../store/homeStore';
import { IApiKeyDetails, ICurrentUser, IUserProfile } from '../../../types/api';
import { DatePFormat } from '../../../utils/StaticVariable';
import UserDetail from './UserDetail';
import styles from './UserProfile.module.scss';

export interface IProps {
  close: () => void;
  user: ICurrentUser;
}

export default function UserProfile({ user, close }: IProps) {
  const loadingDataFromBSApi = useHomeStore(
    (state) => state.loadingDataFromBSApi
  );
  const setUserProfile = useAuthStore((state) => state.setUserProfile);
  const [subscription, setSubscription] = useState<{
    type: string;
    expiredDate: string;
  }>({ type: '', expiredDate: '' });
  const [downloaderApiKey, setDownloaderApiKey] = useState<IApiKeyDetails>();
  const [loadingUserInfo, setLoadingUserInfo] = useState<boolean>(false);
  const updateUserProfileMutation = useUpdateUserProfileMutation();
  const getUserProfile = useGetUserProfile();

  const loading = useMemo(() => {
    return loadingDataFromBSApi || loadingUserInfo;
  }, [loadingDataFromBSApi, loadingUserInfo]);

  const prepareData = useCallback(async () => {
    const result = getUserProfile.data;

    if (!result?.Result) {
      setLoadingUserInfo(false);
      return;
    }

    setUserProfile({
      ...result.Result,
      city:
        result.Result.companyInfo && result.Result.companyInfo.city
          ? result.Result.companyInfo.city
          : '',
      countryId:
        result.Result.companyInfo && result.Result.companyInfo.countryId
          ? result.Result.companyInfo.countryId
          : '',
      companyId: result.Result.companyInfo
        ? result.Result.companyInfo.vatNumber
        : '',
      stateId:
        result.Result.companyInfo && result.Result.companyInfo.stateId
          ? result.Result.companyInfo.stateId
          : '',
      sizeId:
        result.Result.companyInfo && result.Result.companyInfo.sizeId
          ? result.Result.companyInfo.sizeId
          : '', // "999992300",//
      segmentId:
        result.Result.companyInfo && result.Result.companyInfo.segmentId
          ? result.Result.companyInfo.segmentId
          : '', // "999992203"//
      zipcode:
        result.Result.companyInfo && result.Result.companyInfo.postalcode
          ? result.Result.companyInfo.postalcode
          : '',
      address1:
        result.Result.companyInfo && result.Result.companyInfo.address1
          ? result.Result.companyInfo.address1
          : '',
      address2:
        result.Result.companyInfo && result.Result.companyInfo.address2
          ? result.Result.companyInfo.address2
          : '',
      address3:
        result.Result.companyInfo && result.Result.companyInfo.address3
          ? result.Result.companyInfo.address3
          : '',
    });

    setLoadingUserInfo(false);
  }, [getUserProfile.data, setUserProfile]);

  const setSubscriptionDetails = useCallback(() => {
    const subscriptionType = user.subscriptionType;
    const subscriptionExpired = user.subscriptionExpiryDate;
    if (subscriptionExpired && subscriptionType) {
      const subscriptionExpiredDateObj = new Date(subscriptionExpired);
      const formattedSubscriptionDate = moment(
        subscriptionExpiredDateObj
      ).format(DatePFormat);
      setSubscription({
        type: subscriptionType,
        expiredDate: formattedSubscriptionDate,
      });
    }
  }, [user]);

  const getApiKey = useCallback(async () => {
    setSubscriptionDetails();
    const apiKey = await Api.currentUser.getApiKey();
    apiKey && setDownloaderApiKey(apiKey);
  }, [setSubscriptionDetails]);

  async function postUpdate(values: IUserProfile) {
    const data = await updateUserProfileMutation.mutateAsync(values);
    if (data?.Result === 'Profile updated.') {
      message.success('Your profile has been updated successfully!', 10);
    }
  }

  async function requestApiKey() {
    await Api.currentUser.registerDownloaderAPIKey();
    await getApiKey();
  }

  useEffect(() => {
    if (user && user.authenticated) {
      setLoadingUserInfo(true);
      prepareData();
      getApiKey();
    }

    if (user && (user.subscriptionExpiryDate || user.subscriptionType)) {
      setSubscriptionDetails();
    }
  }, [user, getApiKey, prepareData, setSubscriptionDetails]);

  return (
    <Modal
      className={styles.userProfileModal}
      title='Account information'
      open={true}
      maskClosable={false}
      footer={null}
      onCancel={close}
      width={'75vw'}
      zIndex={100}
    >
      {loading ? (
        <div className={styles.spinner}>
          <Spin size='large' />
        </div>
      ) : (
        <UserDetail
          subscription={subscription}
          downloaderApiKey={downloaderApiKey?.apiKey}
          isKeyValid={downloaderApiKey?.isValid}
          requestApiKey={requestApiKey}
          close={close}
          readOnly={false}
          postUpdate={postUpdate}
        />
      )}
    </Modal>
  );
}
